<template>
  <v-card class="rounded-lg white" dir="rtl">
    <v-card-title>مغایرت ورود و فروش شریک</v-card-title>
    <v-card-text>
      <v-row>
        <v-col cols="12" md="5" lg="4">
          <PartnerSelect v-model="partnerId" label="انتخاب شریک" placeholder="یک شریک را انتخاب کنید" clearable />
        </v-col>
      </v-row>

      <div v-if="!partnerId" class="text-center py-12 grey--text">برای دیدن گزارش مغایرت، یک شریک را انتخاب کنید.</div>
      <template v-else>
        <div class="filters">
          <input v-model.trim="search" type="search" placeholder="جست‌وجوی نام کالا، بارکد یا شماره پکینگ" @keyup.enter="applyFilters">
          <label><input v-model="onlyMismatch" type="checkbox" @change="applyFilters"> فقط موارد دارای اختلاف</label>
          <label><input v-model="salesExceedReceipts" type="checkbox" @change="applyFilters"> فروش شریک بیشتر از ورود</label>
          <button :disabled="loading" @click="applyFilters">اعمال فیلتر</button>
          <button :disabled="loading" @click="load">به‌روزرسانی</button>
        </div>

        <p class="explanation">
          ورود فقط از تحویل‌های تأییدشده، پس از کسر تعداد آسیب‌دیده، حساب می‌شود. فروش، جمع فروش آنلاین و حضوری ثبت‌شده به نام همین شریک پس از کسر مرجوعی حضوری است. اختلاف اولیه = ورود − موجودی فعلی محصول − فروش منتسب. جابه‌جایی موجودی و خروج‌های دیگر نیز می‌توانند اختلاف ایجاد کنند.
        </p>

        <div v-if="loading" class="status">در حال دریافت گزارش...</div>
        <div v-else-if="error" class="status status-error">{{ error }} <button @click="load">تلاش دوباره</button></div>
        <template v-else-if="report">
          <div class="totals">
            <div><small>جمع ورود واقعی</small><strong>{{ number(report.totals.received) }}</strong></div>
            <div><small>موجودی فعلی</small><strong>{{ number(report.totals.inventory) }}</strong></div>
            <div><small>فروش به نام شریک</small><strong>{{ number(report.totals.sold) }}</strong></div>
            <div><small>محصول دارای اختلاف</small><strong>{{ number(report.totals.mismatch_products) }}</strong></div>
          </div>
          <p v-if="report.totals.unmatched_products" class="warning">
            {{ number(report.totals.unmatched_products) }} ردیف به محصول کاتالوگ وصل نشده‌اند؛ موجودی و فروش آن‌ها قابل محاسبه نیست.
          </p>
          <div v-if="!report.results.length" class="status">محصولی با این فیلتر پیدا نشد.</div>

          <article v-for="row in report.results" :key="row.key" class="product">
            <header>
              <img v-if="row.image" :src="imageUrl(row.image)" alt="" class="photo">
              <div class="identity"><strong>{{ row.name || 'کالای بدون نام' }}</strong><span dir="ltr">{{ row.barcode || 'بدون بارکد' }}</span></div>
              <span v-if="row.difference !== null" class="difference" :class="row.difference === 0 ? 'balanced' : 'different'">
                اختلاف: {{ number(row.difference) }}
              </span>
              <span v-else class="difference unknown">نیازمند تطبیق کالا</span>
            </header>
            <div class="figures">
              <div><small>کل ورود</small><b>{{ number(row.total_received) }}</b></div>
              <div><small>موجودی فعلی</small><b>{{ row.inventory === null ? '—' : number(row.inventory) }}</b></div>
              <div><small>فروش به نام شریک</small><b>{{ row.sold === null ? '—' : number(row.sold) }}</b></div>
              <div><small>آنلاین / حضوری</small><b>{{ row.online_sold === null ? '—' : `${number(row.online_sold)} / ${number(row.in_store_sold)}` }}</b></div>
            </div>
            <p v-if="row.owner_changed" class="warning">مالک فعلی این محصول {{ row.current_partner_name || 'ثبت نشده' }} است؛ موجودی نمایش‌داده‌شده موجودی کل محصول است.</p>
            <details>
              <summary>ورود در {{ number(row.packings.length) }} پکینگ</summary>
              <div class="packing-table-wrap">
                <table>
                  <thead><tr><th>نوع</th><th>پکینگ</th><th>شماره فاکتور</th><th>ورود تأییدشده</th></tr></thead>
                  <tbody>
                    <tr v-for="packing in row.packings" :key="`${packing.type}-${packing.id}`">
                      <td>{{ packing.type === 'foreign' ? 'خارجی' : 'ایرانی' }}</td>
                      <td><router-link :to="packingLink(packing)">{{ packing.title || `پکینگ ${packing.id}` }}</router-link></td>
                      <td>{{ packing.factor_number || '—' }}</td>
                      <td>{{ number(packing.received) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>
          </article>

          <div v-if="report.count > pageSize" class="pagination">
            <button :disabled="page <= 1 || loading" @click="changePage(page - 1)">صفحه قبل</button>
            <span>صفحه {{ number(page) }} از {{ number(totalPages) }} · {{ number(report.count) }} محصول</span>
            <button :disabled="page >= totalPages || loading" @click="changePage(page + 1)">صفحه بعد</button>
          </div>
        </template>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import PartnerSelect from '@/components/selects/PartnerSelect'
import adminPartnerSelection from './adminPartnerSelectionMixin'
import getPartnerCalculationsApi from './getPartnerCalculationsApi'

export default {
  name: 'AdminPartnerPackingDiscrepancy',
  components: { PartnerSelect },
  mixins: [adminPartnerSelection, getPartnerCalculationsApi],
  data() {
    return { search: '', onlyMismatch: false, salesExceedReceipts: false, page: 1, pageSize: 25, report: null, loading: false, error: '' }
  },
  computed: {
    totalPages() { return this.report ? Math.ceil(this.report.count / this.pageSize) : 0 },
  },
  watch: {
    partnerId() {
      this.page = 1
      this.report = null
      this.load()
    },
  },
  mounted() { this.load() },
  methods: {
    number(value) { return Number(value || 0).toLocaleString('fa-IR') },
    imageUrl(path) { return /^https?:/.test(path) ? path : this.img_source(path) },
    packingLink(packing) {
      return packing.type === 'foreign'
        ? `/panel/chinaEntranceDashboard/${packing.id}`
        : `/panel/domesticEntranceDashboard/${packing.id}`
    },
    applyFilters() { this.page = 1; this.load() },
    changePage(page) { this.page = page; this.load() },
    load() {
      const version = this._requestVersion = (this._requestVersion || 0) + 1
      if (!this.partnerId) { this.report = null; this.error = ''; this.loading = false; return }
      this.loading = true
      this.error = ''
      this.getAdminPartnerPackingDiscrepancy(this.partnerId, {
        q: this.search, only_mismatch: this.onlyMismatch,
        sales_exceed_receipts: this.salesExceedReceipts, page: this.page, page_size: this.pageSize,
      }, data => {
        if (version !== this._requestVersion) return
        this.report = data
        this.loading = false
      }, err => {
        if (version !== this._requestVersion) return
        this.error = (err && err.response && err.response.data && err.response.data.detail) || 'دریافت گزارش ناموفق بود.'
        this.loading = false
      })
    },
  },
}
</script>

<style scoped>
.filters{display:flex;flex-wrap:wrap;align-items:center;gap:10px;margin:12px 0}.filters>input{min-width:240px;flex:1}.filters input[type=search],.filters button,.pagination button{border:1px solid #cbd5e1;border-radius:8px;padding:9px}.filters button,.pagination button{background:#e2e8f0}.filters button:disabled,.pagination button:disabled{opacity:.5}.filters label{display:flex;align-items:center;gap:5px}.explanation{background:#eff6ff;border-radius:8px;padding:12px;line-height:1.9}.totals,.figures{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px;margin:12px 0}.totals>div,.figures>div{background:#f1f5f9;border-radius:8px;padding:10px;display:flex;flex-direction:column;gap:4px}.totals strong{font-size:20px}.product{border:1px solid #cbd5e1;border-radius:12px;padding:14px;margin:12px 0}.product header{display:flex;align-items:center;gap:12px;flex-wrap:wrap}.identity{display:flex;flex:1;min-width:180px;flex-direction:column}.identity span{align-self:flex-start}.photo{width:52px;height:52px;object-fit:contain}.difference{border-radius:20px;padding:5px 10px}.different,.warning{background:#fef3c7;color:#92400e}.balanced{background:#dcfce7;color:#166534}.unknown{background:#e2e8f0}.warning{padding:8px;border-radius:8px;margin:8px 0}.status{text-align:center;padding:35px}.status-error{color:#b91c1c}.packing-table-wrap{overflow-x:auto}table{width:100%;border-collapse:collapse;min-width:500px}td,th{border-bottom:1px solid #e2e8f0;padding:8px;text-align:right}summary{cursor:pointer;font-weight:600}.pagination{display:flex;align-items:center;justify-content:center;gap:14px;margin:20px 0}@media(max-width:700px){.totals,.figures{grid-template-columns:repeat(2,minmax(0,1fr))}}
</style>
